import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Button } from '@uhg-abyss/web/ui/Button';
import { event } from '@uhg-abyss/web/tools/event';
import { isDownloadLink } from '../UHCUpdates';

const Container = styled('div', {
  width: '100%',
  position: 'relative',
  dynamic: ({ imageUrl }) => ({
    background: `url(${imageUrl})`,
    backgroundSize: 'cover !important',
    backgroundRepeat: 'no-repeat !important',
    margin: '16px 0px',
  }),
});

const CardContent = styled('div', {
  position: 'relative',
  width: '50% !important',
  marginLeft: '50%',
  justifyContent: 'space-evenly',
  display: 'block',
  backgroundColor: '$gray2',
  padding: '$md',
  minHeight: '200px',
});

const StyledTitle = styled('label', {
  color: '$primary1',
  fontSize: '20px !important',
  fontWeight: '700 !important',
  minWidth: 150,
  display: 'inline-block',
  marginBottom: 16,
});

const StyledContent = styled('p', {
  width: '90%',
  lineHeight: 1.2,
});

// Helper that mimics ExternalLink’s retrieveName behavior (simplified)
const getLinkDisplayText = (text) =>
  text && text.trim().length ? text : 'Read More';

export const PictureCard = ({
  index,
  title,
  description,
  link,
  linkText,
  imageUrl,
  isDownload,
  downloadContext,
  dataTestid,
  newTab,
  disableAdobeTag,
}) => {
  const sendDownloadClick = isDownloadLink(link, isDownload);
  const displayText = getLinkDisplayText(linkText);

  const handleClick = (e) => {
    // If Button renders an <a>, let us still control the target navigation logic
    // but not duplicate if browser already handles it. We ensure proper target/rel attributes below.
    if (!link) return;

    // Fire download analytics if needed
    if (sendDownloadClick) {
      event('DOWNLOAD_CLICK', {
        fileName: displayText,
        fileURL: link,
        fileType: downloadContext,
      });
    }

    // Billboard / bumper analytics
    event('BUMPER_STICKER_AND_BILLBOARD_LINK_CLICKS', {
      interactionName: title,
      interactionValue: displayText,
    });

    // ExternalLink equivalent analytics unless disabled
    if (!disableAdobeTag) {
      event('EXIT_LINK_NAVIGATE', { linkText: displayText, linkUrl: link });
    }

    // If Button component does NOT automatically respect target, manually open
    // (Keeping this defensive: only intervene if newTab true & no native anchor open expected)
    if (newTab) {
      // Prevent same-tab navigation if underlying element is an anchor
      if (e && typeof e.preventDefault === 'function') {
        e.preventDefault();
      }
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <React.Fragment>
      <Container
        data-testid={dataTestid}
        imageUrl={imageUrl}
        direction="column"
        justify="center"
      >
        <CardContent>
          <StyledTitle>{title}</StyledTitle>
          <If condition={description}>
            <StyledContent>{description}</StyledContent>
          </If>
          <Button
            onClick={handleClick}
            href={link}
            // Provide target/rel so if the underlying Button delegates to an <a>,
            // browser handles new-tab open without JS fallback.
            target={newTab ? '_blank' : undefined}
            rel={newTab ? 'noopener noreferrer' : undefined}
          >
            {displayText}
          </Button>
        </CardContent>
      </Container>
    </React.Fragment>
  );
};

PictureCard.propTypes = {
  index: PropTypes.string.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  linkText: PropTypes.string,
  link: PropTypes.string,
  imageUrl: PropTypes.string,
  isDownload: PropTypes.bool,
  downloadContext: PropTypes.string,
  dataTestid: PropTypes.string,
  newTab: PropTypes.bool,
  disableAdobeTag: PropTypes.bool,
};

PictureCard.defaultProps = {
  title: '',
  description: '',
  linkText: '',
  link: '',
  imageUrl: '',
  isDownload: false,
  downloadContext: '',
  dataTestid: '',
  newTab: true,
  disableAdobeTag: false,
};
